import type { Store } from '../../../../services/authService';
import type {
    CounterScanningMethodKey,
    StoreCountingDirection,
    StoreFormData,
} from '../../../../types/features/storeDetails/storeForm';

export const COUNTER_SCANNING_METHOD_OPTIONS: Array<{
    key: CounterScanningMethodKey;
    label: string;
}> = [
    { key: 'camera', label: 'Camera' },
    { key: 'scanner', label: 'Scanner' },
    { key: 'lottoreco_scanner', label: 'LottoReco Scanner' },
    { key: 'ticket_balance_detail_report', label: 'Submit Ticket Balance Detail Report' },
    { key: 'scanbot_camera', label: 'Scanbot Camera' },
];

export const DEFAULT_COUNTER_SCANNING_METHODS = COUNTER_SCANNING_METHOD_OPTIONS.map(
    (option) => option.key
);
export const LOTTORECO_POS_TYPE = 'LottoReco';

const normalizeTimeInputValue = (value?: string | null): string => {
    if (!value) {
        return '20:45';
    }

    return value.slice(0, 5);
};

const normalizeCountingDirection = (value?: string | null): StoreCountingDirection => (
    value === 'forwards' || value === 'backwards' ? value : 'backwards'
);

const normalizeCounterScanningMethods = (
    value?: Store['scanning_methods'] | null
): CounterScanningMethodKey[] => {
    const counter = value?.counter;
    if (!Array.isArray(counter) || counter.length === 0) {
        return [...DEFAULT_COUNTER_SCANNING_METHODS];
    }

    const allowed = new Set(DEFAULT_COUNTER_SCANNING_METHODS);
    const recognized = counter.filter((method): method is CounterScanningMethodKey => (
        allowed.has(method as CounterScanningMethodKey)
    ));

    return recognized.length > 0 ? recognized : [...DEFAULT_COUNTER_SCANNING_METHODS];
};

export const getDefaultStoreFormValues = (): StoreFormData => ({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    latitude: null,
    longitude: null,
    status: 'active',
    brand: '',
    pos_type: '',
    cash_register_enabled: false,
    direction: 'backwards',
    store_closing_time: '20:45',
    backstock_audit_frequency_seconds: null,
    scratcher_reconciliation_cutoff_offset_minutes: 0,
    pos_integration_interest: false,
    lotto_authorized: false,
    enable_audit_confirmation_summary: false,
    require_audit_mismatch_note: false,
    logo_image_url: '',
    lottery_reseller_id: '',
    pos_payout_mapping: {
        financial_event: [],
        transaction_line: [],
        lotto_merch_description: [],
    },
    scanning_methods: {
        counter: [...DEFAULT_COUNTER_SCANNING_METHODS],
    },
});

export const mapStoreToFormValues = (store: Store | null): StoreFormData => {
    if (!store) {
        return getDefaultStoreFormValues();
    }

    return {
        name: store.name || '',
        address_line1: store.address_line1 || store.address || '',
        address_line2: store.address_line2 || '',
        city: store.city || '',
        state: store.state || '',
        postal_code: store.postal_code || store.zip_code || '',
        country: store.country || '',
        latitude: store.latitude ? parseFloat(store.latitude) : null,
        longitude: store.longitude ? parseFloat(store.longitude) : null,
        status: store.status || 'active',
        brand: store.brand || '',
        pos_type: store.pos_type || '',
        cash_register_enabled: store.cash_register_enabled === true,
        direction: normalizeCountingDirection(store.direction),
        store_closing_time: normalizeTimeInputValue(store.store_closing_time),
        backstock_audit_frequency_seconds: store.backstock_audit_frequency_seconds ?? null,
        scratcher_reconciliation_cutoff_offset_minutes: store.scratcher_reconciliation_cutoff_offset_minutes ?? 0,
        pos_integration_interest: store.pos_integration_interest || false,
        lotto_authorized: store.lotto_authorized || false,
        enable_audit_confirmation_summary: store.enable_audit_confirmation_summary === true,
        require_audit_mismatch_note: store.require_audit_mismatch_note === true,
        logo_image_url: store.logo_image_url || '',
        lottery_reseller_id: store.lottery_reseller_id || '',
        pos_payout_mapping: {
            financial_event: store.pos_payout_mapping?.financial_event ?? [],
            transaction_line: store.pos_payout_mapping?.transaction_line ?? [],
            lotto_merch_description: store.pos_payout_mapping?.lotto_merch_description ?? [],
        },
        scanning_methods: {
            counter: normalizeCounterScanningMethods(store.scanning_methods),
        },
    };
};

export const normalizeStoreUpdatePayload = (data: StoreFormData) => ({
    ...data,
    latitude: data.latitude === null ? undefined : data.latitude,
    longitude: data.longitude === null ? undefined : data.longitude,
    scanning_methods: {
        counter: data.scanning_methods.counter,
    },
});
